import type { TranslationTable } from "@repo/shared/types";

const EN_GB = {
  users: "Users",
  usersDescription: "Top 15 by AUM · click a column to rank by it",
  colUser: "User",
  colAum: "AUM",
  colInflow: "Saving inflow / yr",
  colActivities: "Activities",
  colMeetings: "Meetings",
  colCalls: "Calls",
  colNotes: "Notes",
  colFlowEvents: "Flow events",
  showingUsers: (shown: number, total: number) => `Showing ${shown} of ${total} users`,
  amountsIn: (currency: string) => `Amounts in ${currency} · activity columns follow the period`,
  noUsers: "No users yet.",
  clientCount: (n: string) => `${n} clients`,
  clientCountOne: "1 client",
};

// All 17 locales (QL-17 plan keeps them); the type makes each one match EN_GB.
export const T: TranslationTable<typeof EN_GB> = {
  en_GB: EN_GB,
  fr_FR: {
    users: "Utilisateurs",
    usersDescription: "Top 15 par encours · cliquez sur une colonne pour trier",
    colUser: "Utilisateur",
    colAum: "Encours",
    colInflow: "Épargne / an",
    colActivities: "Activités",
    colMeetings: "Rendez-vous",
    colCalls: "Appels",
    colNotes: "Notes",
    colFlowEvents: "Événements de parcours",
    showingUsers: (shown, total) => `${shown} utilisateurs affichés sur ${total}`,
    amountsIn: (currency) => `Montants en ${currency} · les colonnes d'activité suivent la période`,
    noUsers: "Aucun utilisateur pour le moment.",
    clientCount: (n) => `${n} clients`,
    clientCountOne: "1 client",
  },
  es_ES: {
    users: "Usuarios",
    usersDescription: "Top 15 por patrimonio gestionado · haz clic en una columna para ordenar",
    colUser: "Usuario",
    colAum: "Patrimonio",
    colInflow: "Ahorro / año",
    colActivities: "Actividades",
    colMeetings: "Reuniones",
    colCalls: "Llamadas",
    colNotes: "Notas",
    colFlowEvents: "Eventos de flujo",
    showingUsers: (shown, total) => `Mostrando ${shown} de ${total} usuarios`,
    amountsIn: (currency) =>
      `Importes en ${currency} · las columnas de actividad siguen el periodo`,
    noUsers: "Aún no hay usuarios.",
    clientCount: (n) => `${n} clientes`,
    clientCountOne: "1 cliente",
  },
  pt_BR: {
    users: "Usuários",
    usersDescription: "Top 15 por patrimônio sob gestão · clique em uma coluna para ordenar",
    colUser: "Usuário",
    colAum: "Patrimônio",
    colInflow: "Aportes / ano",
    colActivities: "Atividades",
    colMeetings: "Reuniões",
    colCalls: "Ligações",
    colNotes: "Notas",
    colFlowEvents: "Eventos de fluxo",
    showingUsers: (shown, total) => `Exibindo ${shown} de ${total} usuários`,
    amountsIn: (currency) => `Valores em ${currency} · as colunas de atividade seguem o período`,
    noUsers: "Nenhum usuário ainda.",
    clientCount: (n) => `${n} clientes`,
    clientCountOne: "1 cliente",
  },
  de_DE: {
    users: "Benutzer",
    usersDescription: "Top 15 nach verwaltetem Vermögen · Spalte anklicken, um danach zu sortieren",
    colUser: "Benutzer",
    colAum: "Verw. Vermögen",
    colInflow: "Sparzuflüsse / Jahr",
    colActivities: "Aktivitäten",
    colMeetings: "Termine",
    colCalls: "Anrufe",
    colNotes: "Notizen",
    colFlowEvents: "Flow-Ereignisse",
    showingUsers: (shown, total) => `${shown} von ${total} Benutzern`,
    amountsIn: (currency) => `Beträge in ${currency} · Aktivitätsspalten folgen dem Zeitraum`,
    noUsers: "Noch keine Benutzer.",
    clientCount: (n) => `${n} Kunden`,
    clientCountOne: "1 Kunde",
  },
  it_IT: {
    users: "Utenti",
    usersDescription: "Top 15 per patrimonio gestito · fai clic su una colonna per ordinare",
    colUser: "Utente",
    colAum: "Patrimonio",
    colInflow: "Risparmio / anno",
    colActivities: "Attività",
    colMeetings: "Riunioni",
    colCalls: "Chiamate",
    colNotes: "Note",
    colFlowEvents: "Eventi di flusso",
    showingUsers: (shown, total) => `${shown} di ${total} utenti visualizzati`,
    amountsIn: (currency) => `Importi in ${currency} · le colonne di attività seguono il periodo`,
    noUsers: "Ancora nessun utente.",
    clientCount: (n) => `${n} clienti`,
    clientCountOne: "1 cliente",
  },
  ar: {
    users: "المستخدمون",
    usersDescription: "أعلى 15 حسب الأصول المُدارة · انقر على عمود للترتيب حسبه",
    colUser: "المستخدم",
    colAum: "الأصول المُدارة",
    colInflow: "تدفقات الادخار / سنة",
    colActivities: "الأنشطة",
    colMeetings: "الاجتماعات",
    colCalls: "المكالمات",
    colNotes: "الملاحظات",
    colFlowEvents: "أحداث المسارات",
    showingUsers: (shown, total) => `عرض ${shown} من ${total} مستخدمين`,
    amountsIn: (currency) => `المبالغ بعملة ${currency} · أعمدة النشاط تتبع الفترة المحددة`,
    noUsers: "لا يوجد مستخدمون بعد.",
    clientCount: (n) => `${n} عملاء`,
    clientCountOne: "عميل واحد",
  },
  ja_JP: {
    users: "ユーザー",
    usersDescription: "AUM 上位 15 名 · 列をクリックすると並べ替えます",
    colUser: "ユーザー",
    colAum: "AUM",
    colInflow: "積立流入額／年",
    colActivities: "アクティビティ",
    colMeetings: "面談",
    colCalls: "通話",
    colNotes: "メモ",
    colFlowEvents: "フローイベント",
    showingUsers: (shown, total) => `${total} 名中 ${shown} 名を表示`,
    amountsIn: (currency) => `金額は ${currency} · アクティビティ列は選択期間に連動`,
    noUsers: "ユーザーはまだいません。",
    clientCount: (n) => `顧客 ${n} 名`,
    clientCountOne: "顧客 1 名",
  },
  hi_IN: {
    users: "उपयोगकर्ता",
    usersDescription: "AUM के अनुसार शीर्ष 15 · रैंक बदलने के लिए किसी कॉलम पर क्लिक करें",
    colUser: "उपयोगकर्ता",
    colAum: "AUM",
    colInflow: "बचत अंतर्वाह / वर्ष",
    colActivities: "गतिविधियाँ",
    colMeetings: "मीटिंग",
    colCalls: "कॉल",
    colNotes: "नोट्स",
    colFlowEvents: "फ़्लो इवेंट",
    showingUsers: (shown, total) => `${total} में से ${shown} उपयोगकर्ता दिखाए जा रहे हैं`,
    amountsIn: (currency) => `राशियाँ ${currency} में · गतिविधि कॉलम चुनी गई अवधि के अनुसार हैं`,
    noUsers: "अभी कोई उपयोगकर्ता नहीं है।",
    clientCount: (n) => `${n} क्लाइंट`,
    clientCountOne: "1 क्लाइंट",
  },
  zh_CN: {
    users: "用户",
    usersDescription: "按 AUM 排名前 15 · 点击列可按该列排序",
    colUser: "用户",
    colAum: "AUM",
    colInflow: "定投流入 / 年",
    colActivities: "活动",
    colMeetings: "会议",
    colCalls: "通话",
    colNotes: "备注",
    colFlowEvents: "流程事件",
    showingUsers: (shown, total) => `显示 ${total} 位用户中的 ${shown} 位`,
    amountsIn: (currency) => `金额单位：${currency} · 活动列随所选期间变化`,
    noUsers: "暂无用户。",
    clientCount: (n) => `${n} 位客户`,
    clientCountOne: "1 位客户",
  },
  zh_TW: {
    users: "使用者",
    usersDescription: "依 AUM 排名前 15 · 點選欄位即可依該欄排序",
    colUser: "使用者",
    colAum: "AUM",
    colInflow: "定期定額流入 / 年",
    colActivities: "活動",
    colMeetings: "會議",
    colCalls: "通話",
    colNotes: "備註",
    colFlowEvents: "流程事件",
    showingUsers: (shown, total) => `顯示 ${total} 位使用者中的 ${shown} 位`,
    amountsIn: (currency) => `金額單位：${currency} · 活動欄位依所選期間變動`,
    noUsers: "尚無使用者。",
    clientCount: (n) => `${n} 位客戶`,
    clientCountOne: "1 位客戶",
  },
  nl_NL: {
    users: "Gebruikers",
    usersDescription: "Top 15 op beheerd vermogen · klik op een kolom om daarop te rangschikken",
    colUser: "Gebruiker",
    colAum: "Beheerd vermogen",
    colInflow: "Spaarinleg / jr",
    colActivities: "Activiteiten",
    colMeetings: "Afspraken",
    colCalls: "Gesprekken",
    colNotes: "Notities",
    colFlowEvents: "Flowgebeurtenissen",
    showingUsers: (shown, total) => `${shown} van ${total} gebruikers weergegeven`,
    amountsIn: (currency) => `Bedragen in ${currency} · activiteitskolommen volgen de periode`,
    noUsers: "Nog geen gebruikers.",
    clientCount: (n) => `${n} klanten`,
    clientCountOne: "1 klant",
  },
  nb_NO: {
    users: "Brukere",
    usersDescription:
      "Topp 15 etter forvaltningskapital · klikk en kolonne for å rangere etter den",
    colUser: "Bruker",
    colAum: "Forvaltningskapital",
    colInflow: "Sparing / år",
    colActivities: "Aktiviteter",
    colMeetings: "Møter",
    colCalls: "Samtaler",
    colNotes: "Notater",
    colFlowEvents: "Flythendelser",
    showingUsers: (shown, total) => `Viser ${shown} av ${total} brukere`,
    amountsIn: (currency) => `Beløp i ${currency} · aktivitetskolonnene følger perioden`,
    noUsers: "Ingen brukere ennå.",
    clientCount: (n) => `${n} kunder`,
    clientCountOne: "1 kunde",
  },
  sv_SE: {
    users: "Användare",
    usersDescription:
      "Topp 15 efter förvaltat kapital · klicka på en kolumn för att rangordna efter den",
    colUser: "Användare",
    colAum: "Förvaltat kapital",
    colInflow: "Sparande / år",
    colActivities: "Aktiviteter",
    colMeetings: "Möten",
    colCalls: "Samtal",
    colNotes: "Anteckningar",
    colFlowEvents: "Flödeshändelser",
    showingUsers: (shown, total) => `Visar ${shown} av ${total} användare`,
    amountsIn: (currency) => `Belopp i ${currency} · aktivitetskolumnerna följer perioden`,
    noUsers: "Inga användare ännu.",
    clientCount: (n) => `${n} kunder`,
    clientCountOne: "1 kund",
  },
  fi_FI: {
    users: "Käyttäjät",
    usersDescription:
      "15 suurinta hallinnoitavien varojen mukaan · järjestä sarakkeen mukaan napsauttamalla",
    colUser: "Käyttäjä",
    colAum: "Hallinnoitavat varat",
    colInflow: "Säästöt / v",
    colActivities: "Toiminnot",
    colMeetings: "Tapaamiset",
    colCalls: "Puhelut",
    colNotes: "Muistiinpanot",
    colFlowEvents: "Prosessitapahtumat",
    showingUsers: (shown, total) => `Näytetään ${shown}/${total} käyttäjää`,
    amountsIn: (currency) =>
      `Summat valuutassa ${currency} · toimintosarakkeet noudattavat valittua jaksoa`,
    noUsers: "Ei vielä käyttäjiä.",
    clientCount: (n) => `${n} asiakasta`,
    clientCountOne: "1 asiakas",
  },
  da_DK: {
    users: "Brugere",
    usersDescription:
      "Top 15 efter formue under forvaltning · klik på en kolonne for at rangere efter den",
    colUser: "Bruger",
    colAum: "Formue under forvaltning",
    colInflow: "Opsparing / år",
    colActivities: "Aktiviteter",
    colMeetings: "Møder",
    colCalls: "Opkald",
    colNotes: "Noter",
    colFlowEvents: "Forløbshændelser",
    showingUsers: (shown, total) => `Viser ${shown} af ${total} brugere`,
    amountsIn: (currency) => `Beløb i ${currency} · aktivitetskolonnerne følger perioden`,
    noUsers: "Ingen brugere endnu.",
    clientCount: (n) => `${n} kunder`,
    clientCountOne: "1 kunde",
  },
  is_IS: {
    users: "Notendur",
    usersDescription: "Efstu 15 eftir eignum í stýringu · smelltu á dálk til að raða eftir honum",
    colUser: "Notandi",
    colAum: "Eignir í stýringu",
    colInflow: "Sparnaður / ár",
    colActivities: "Aðgerðir",
    colMeetings: "Fundir",
    colCalls: "Símtöl",
    colNotes: "Minnispunktar",
    colFlowEvents: "Ferlisatburðir",
    showingUsers: (shown, total) => `Sýnir ${shown} af ${total} notendum`,
    amountsIn: (currency) => `Upphæðir í ${currency} · aðgerðadálkar fylgja tímabilinu`,
    noUsers: "Engir notendur enn.",
    clientCount: (n) => `${n} viðskiptavinir`,
    clientCountOne: "1 viðskiptavinur",
  },
};
